import React, { useState } from 'react';
import { Button, Tooltip, Upload, message } from 'antd';
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {RequestAddVersion} from "../../../Actions/documents.action";
import {
    fetchCommercialUsage,
    openCommercialGateModal,
} from "../../../Actions/commercialControl.action";
import { serializeMetadataListForApi } from "../../../utils/metadataNormalizer";
import {
    buildAcceptAttributeFromFormats,
    buildLockedCommercialDecision,
    canStartCompleteAnalysisFromUsage,
    getAllowedUploadFormats,
    getUsageByTypeFromSnapshot,
} from "../../../utils/commercialCapabilities";

const toMetadataCsv = (value) => {
    return serializeMetadataListForApi(value) ?? null;
};

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
        const base64Content = String(event.target?.result || "").split(",")[1];
        resolve(base64Content || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const AddVersionButton = ({ analyseId }) => {
    const { t } = useTranslation('review');
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const mainDocument = useSelector(state => state.stage.main_document);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const commercialCapabilities = useSelector((state) => state.commercialControl?.capabilities || {});
    const reduxUsageByType = commercialControl.usageByType || {};
    const usageByType = Object.keys(reduxUsageByType).length
        ? reduxUsageByType
        : getUsageByTypeFromSnapshot(commercialControl.usageSnapshot);
    const hasUsageSnapshot = !!commercialControl.usageSnapshot || Object.keys(usageByType).length > 0;
    const canStartNewAnalysis = canStartCompleteAnalysisFromUsage(usageByType);
    const blockedByUsage = hasUsageSnapshot && !canStartNewAnalysis;
    const allowedUploadFormats = getAllowedUploadFormats(commercialCapabilities) || ["pdf"];
    const accept = buildAcceptAttributeFromFormats(allowedUploadFormats) || ".pdf";

    const openNoAnalysisModal = () => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision("complete_analysis", {
            titleKey: "commercial.locked.complete_analysis.title",
            bodyKey: "commercial.locked.complete_analysis.body",
            titleFr: "Nouveau dossier indisponible",
            bodyFr: "Votre plan ne permet pas de créer un nouveau dossier de revue pour le moment.",
            titleEn: "New case unavailable",
            bodyEn: "Your plan does not allow creating a new review case right now.",
            metadata_json: {
                action: "add_document_version",
                usage_type: "complete_analysis",
                analysis_id: analyseId,
            },
        })));
    };

    const beforeUpload = (file) => {
        const run = async () => {
            setLoading(true);

            try {
                const usageSnapshot = await dispatch(fetchCommercialUsage());
                const refreshedUsageByType = getUsageByTypeFromSnapshot(usageSnapshot);
                if (!canStartCompleteAnalysisFromUsage(refreshedUsageByType)) {
                    openNoAnalysisModal();
                    return;
                }

                const base64Content = await readFileAsBase64(file);
                const versionData = {
                    analyse_id: analyseId,
                    type: mainDocument?.type || 'PRINCIPAL',
                    document_type: mainDocument?.document_type || null,
                    title: file.name,
                    file_content: base64Content,

                    // On reprend les métadonnées du document principal
                    industry: toMetadataCsv(mainDocument?.industry),
                    description: mainDocument?.description,
                    party_role: mainDocument?.party_role,
                    studied_party_name: mainDocument?.studied_party_name,
                    jurisdiction: toMetadataCsv(mainDocument?.jurisdiction),

                    // Champs vides pour nouvelle version
                    content_html: "",
                    comments: []
                };

                await dispatch(RequestAddVersion(versionData));

            } catch (error) {
                message.error(t('version_actions.error'));
                console.error("Erreur:", error);
            } finally {
                setLoading(false);
            }
        };

        run();
        return false; // Empêche l'upload automatique
    };

    const button = (
        <Button
            icon={blockedByUsage ? <LockOutlined /> : <PlusOutlined />}
            loading={loading || commercialControl.isLoadingUsage}
        >
            {t('version_actions.new')}
        </Button>
    );

    return (
        <Tooltip
            title={blockedByUsage
                ? t("commercial.locked.complete_analysis.body", {
                    defaultValue: "Votre plan ne permet pas de créer un nouveau dossier de revue pour le moment.",
                })
                : null}
        >
            <span>
                <Upload
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept={accept}
                    disabled={loading}
                >
                    {button}
                </Upload>
            </span>
        </Tooltip>
    );
};

export default AddVersionButton;
